import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import aviso, { textoDoErro } from '../lib/aviso';
import {
    LuPlus, LuSearch, LuPencil, LuEye, LuTrash2, LuChevronLeft, LuChevronRight, LuX, LuInfo,
} from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import RecordPanel, { PanelSection, Switch } from '../components/RecordPanel';
import SecaoHistorico from '../components/historico/SecaoHistorico';
import { ListRow, PerPage, RowAction, RowActions, RowConfirm, CardConfirm, ListCard, ListCards } from '../components/ListKit';
import Dialog from '../components/Dialog';
import Select from '../components/Select';
import '../components/ListKit.css';
import { useCelular } from '../hooks/useCelular';
import { useRecorte } from '../hooks/useRecorte';
import './CamposPersonalizadosPage.css';
import CampoPersonalizadoService, {
    TipoCampoPersonalizado, type CampoPersonalizado,
} from '../services/CampoPersonalizadoService';

const POR_PAGINA = [25, 50, 100];
type Modo = 'ver' | 'editar';

const TIPOS: { valor: TipoCampoPersonalizado; rotulo: string; ajuda: string }[] = [
    { valor: TipoCampoPersonalizado.Texto, rotulo: 'Texto', ajuda: 'Uma linha livre.' },
    { valor: TipoCampoPersonalizado.Numero, rotulo: 'Número', ajuda: 'Só números.' },
    { valor: TipoCampoPersonalizado.Data, rotulo: 'Data', ajuda: 'Seletor de data.' },
    { valor: TipoCampoPersonalizado.Booleano, rotulo: 'Sim/Não', ajuda: 'Um interruptor.' },
    { valor: TipoCampoPersonalizado.Selecao, rotulo: 'Seleção', ajuda: 'Escolha entre opções que você define.' },
];

const rotuloTipo = (t: number) => TIPOS.find(x => x.valor === t)?.rotulo ?? '—';

interface Rascunho {
    id: number | null;
    nome: string;
    tipo: TipoCampoPersonalizado;
    opcoes: string[];
    obrigatorio: boolean;
    ordem: number;
}

const VAZIO: Rascunho = {
    id: null, nome: '', tipo: TipoCampoPersonalizado.Texto,
    opcoes: [], obrigatorio: false, ordem: 0,
};

const CamposPersonalizadosPage = () => {
    const celular = useCelular();
    const [campos, setCampos] = useState<CampoPersonalizado[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    /* Recorte de trabalho, na sessao da aba. */
    const [recorte, gravarRecorte] = useRecorte('cp:recorte', {
        busca: '', pagina: 1, porPagina: 25,
    });
    const [busca, setBusca] = useState(recorte.busca);
    const [pagina, setPagina] = useState(recorte.pagina);
    const [porPagina, setPorPagina] = useState(recorte.porPagina);
    useEffect(() => {
        gravarRecorte({ busca, pagina, porPagina });
    }, [busca, pagina, porPagina, gravarRecorte]);

    const [aberto, setAberto] = useState<CampoPersonalizado | null>(null);
    const [modo, setModo] = useState<Modo>('ver');
    const [rascunho, setRascunho] = useState<Rascunho>(VAZIO);
    const [novaOpcao, setNovaOpcao] = useState('');

    /* A lixeira da linha pergunta NA linha; o rodapé do painel abre o
       `<Dialog>`, porque ali não existe linha para pintar. */
    const [aConfirmar, setAConfirmar] = useState<number | null>(null);
    const [confirmarNoPainel, setConfirmarNoPainel] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [erros, setErros] = useState<Record<string, string>>({});

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            setCampos(await CampoPersonalizadoService.buscarTodos());
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível carregar os campos.'));
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);
    // Mudar um filtro volta para a pagina 1 - mas nao na montagem, senao a
    // pagina restaurada da sessao se perde.
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, porPagina]);

    const ordenados = useMemo(
        () => [...campos].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)), [campos]);

    const filtrados = useMemo(() => {
        const t = busca.trim().toLowerCase();
        return t
            ? ordenados.filter(c => [c.nome, c.chave].some(v => (v || '').toLowerCase().includes(t)))
            : ordenados;
    }, [ordenados, busca]);

    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = filtrados.slice(inicio, inicio + porPagina);

    const abrirVer = (c: CampoPersonalizado) => { setAberto(c); setModo('ver'); setErros({}); };

    const abrirEdicao = (c: CampoPersonalizado) => {
        setAberto(c);
        setRascunho({
            id: c.campoPersonalizadoId, nome: c.nome, tipo: c.tipo,
            opcoes: c.opcoes ?? [], obrigatorio: c.obrigatorio, ordem: c.ordem ?? 0,
        });
        setModo('editar'); setErros({}); setNovaOpcao('');
    };

    const abrirNovo = () => {
        setAberto({ campoPersonalizadoId: 0 } as CampoPersonalizado);
        // Entra no fim da lista: ordem é a posição no formulário do negócio.
        setRascunho({ ...VAZIO, ordem: campos.length });
        setModo('editar'); setErros({}); setNovaOpcao('');
    };

    const fechar = () => { setAberto(null); setErros({}); };
    const ehNovo = modo === 'editar' && rascunho.id === null;
    const ehSelecao = rascunho.tipo === TipoCampoPersonalizado.Selecao;

    const adicionarOpcao = () => {
        const v = novaOpcao.trim();
        if (!v) return;
        if (rascunho.opcoes.includes(v)) { aviso.atencao('Essa opção já está na lista.'); return; }
        setRascunho(r => ({ ...r, opcoes: [...r.opcoes, v] }));
        setNovaOpcao('');
        setErros(e => ({ ...e, opcoes: '' }));
    };

    const salvar = async () => {
        const e: Record<string, string> = {};
        if (!rascunho.nome.trim()) e.nome = 'Informe o nome do campo.';
        /* Sem opção nenhuma, um campo de seleção é um select vazio na tela do
           negócio — o usuário abre e não tem o que escolher. */
        if (ehSelecao && rascunho.opcoes.length === 0) {
            e.opcoes = 'Um campo de seleção precisa de pelo menos uma opção.';
        }
        if (Object.keys(e).length) { setErros(e); return; }

        setSalvando(true);
        try {
            const dto = {
                nome: rascunho.nome.trim(),
                tipo: rascunho.tipo,
                // Só o tipo Seleção guarda opções; os outros mandam null.
                opcoes: ehSelecao ? rascunho.opcoes : null,
                obrigatorio: rascunho.obrigatorio,
                ordem: rascunho.ordem,
            };
            if (rascunho.id) {
                await CampoPersonalizadoService.atualizar({ campoPersonalizadoId: rascunho.id, ...dto });
                aviso.ok('Campo atualizado.');
            } else {
                await CampoPersonalizadoService.criar(dto);
                aviso.ok('Campo criado.');
            }
            await carregar(); fechar();
        } catch (err: any) {
            aviso.erro(textoDoErro(err, 'Não foi possível salvar o campo.'));
        } finally {
            setSalvando(false);
        }
    };

    /* `alvo` opcional porque a exclusão tem duas portas: a lixeira da linha
       (que manda o campo) e o rodapé do painel (que usa o que está em edição). */
    const excluir = async (alvo?: CampoPersonalizado) => {
        const id = alvo?.campoPersonalizadoId ?? rascunho.id;
        if (!id) return;
        setSalvando(true);
        try {
            await CampoPersonalizadoService.remover(id);
            aviso.ok('Campo excluído.');
            await carregar(); fechar();
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível excluir o campo.'));
        } finally {
            setSalvando(false);
            setAConfirmar(null);
            setConfirmarNoPainel(false);
        }
    };

    const COLUNAS = '1.6fr 1.2fr .9fr .9fr 1.3fr 78px';

    /* O aviso de "nada aqui" e o rodapé de paginação servem à tabela (no
       computador) e aos cartões (no celular) — por isso saem do meio do JSX. */
    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {campos.length === 0 ? 'Nenhum campo personalizado' : 'Nada com esse termo'}
            </div>
            <p className="lk-blank-text">
                {campos.length === 0
                    ? 'Use para guardar no negócio o que é específico do seu processo e não cabe nos campos padrão.'
                    : 'Ajuste a busca para ver os outros.'}
            </p>
            {campos.length === 0 && (
                <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                    <LuPlus size={14} /> Novo campo
                </button>
            )}
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
            <span className="lk-range">
                {inicio + 1}–{Math.min(inicio + porPagina, filtrados.length)} de {filtrados.length}
            </span>
            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} />
                </button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} />
                </button>
            </span>
        </div>
    );

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Campos personalizados"
                subtitle="Campos extras que aparecem no cadastro de cada negócio."
                /* Lista vazia: a porta é a do aviso, no meio da tela. */
                actions={!isLoading && campos.length === 0 ? undefined :
                    <div className="lk-actions">
                        <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                            <LuPlus size={14} /> Novo campo
                        </button>
                    </div>
                }
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input type="search" placeholder="Buscar por nome ou chave…"
                            value={busca} onChange={e => setBusca(e.target.value)} />
                    </label>
                    <span className="lk-count">
                        {campos.length} {campos.length === 1 ? 'campo' : 'campos'}
                    </span>
                </div>

                {/* Celular: a grade de seis colunas não cabe na largura do
                    telefone, então a linha vira cartão — o rótulo, o tipo e o
                    que decide se o campo trava o negócio. Chave e opções, uma a
                    uma, ficam no painel. */}
                {celular ? (
                    <>
                        <ListCards>
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(c => {
                                    const ops = c.opcoes ?? [];
                                    return aConfirmar === c.campoPersonalizadoId ? (
                                        <CardConfirm key={c.campoPersonalizadoId}
                                            question={`Excluir "${c.nome}"? Os valores já preenchidos somem da tela.`}
                                            busy={salvando}
                                            onCancel={() => setAConfirmar(null)}
                                            onConfirm={() => excluir(c)} />
                                    ) : (
                                        <ListCard
                                            key={c.campoPersonalizadoId}
                                            titulo={c.nome}
                                            apoio={rotuloTipo(c.tipo)}
                                            estado={
                                                <>
                                                    {c.obrigatorio
                                                        ? <span className="cp-tag is-req">Obrigatório</span>
                                                        : <span>opcional</span>}
                                                    {c.tipo === TipoCampoPersonalizado.Selecao && (ops.length === 0
                                                        ? <span className="cp-tag is-alerta">sem opções</span>
                                                        : <span>· {ops.length} {ops.length === 1 ? 'opção' : 'opções'}</span>)}
                                                </>
                                            }
                                            selected={aberto?.campoPersonalizadoId === c.campoPersonalizadoId}
                                            label={`Abrir ${c.nome}`}
                                            onOpen={() => abrirVer(c)}
                                            acoes={
                                                <RowAction icon={<LuTrash2 size={15} />} label="Excluir campo" danger
                                                    onClick={() => setAConfirmar(c.campoPersonalizadoId)} />
                                            }
                                        />
                                    );
                                })}
                        </ListCards>
                        {!isLoading && filtrados.length === 0 && listaVazia}
                        {!isLoading && filtrados.length > 0 && rodape}
                    </>
                ) : (
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            <div className="lk-head">Campo</div>
                            <div className="lk-head">Chave</div>
                            <div className="lk-head">Tipo</div>
                            <div className="lk-head">Obrigatório</div>
                            <div className="lk-head">Opções</div>
                            <div className="lk-head lk-head-acts">Ações</div>
                        </div>
                        <div className="lk-body">
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(c => {
                                    const ops = c.opcoes ?? [];
                                    if (aConfirmar === c.campoPersonalizadoId) return (
                                        <RowConfirm key={c.campoPersonalizadoId}
                                            question={`Excluir "${c.nome}"? Os valores já preenchidos somem da tela.`}
                                            busy={salvando}
                                            onCancel={() => setAConfirmar(null)}
                                            onConfirm={() => excluir(c)} />
                                    );
                                    return (
                                        <ListRow key={c.campoPersonalizadoId} columns={COLUNAS}
                                            selected={aberto?.campoPersonalizadoId === c.campoPersonalizadoId}>
                                            <span className="lk-cell lk-strong"><span>{c.nome}</span></span>
                                            <span className="lk-cell cp-chave"><span>{c.chave || '—'}</span></span>
                                            <span className="lk-cell lk-muted"><span>{rotuloTipo(c.tipo)}</span></span>
                                            <span className="lk-cell">
                                                {c.obrigatorio
                                                    ? <span className="cp-tag is-req">Sim</span>
                                                    : <span className="lk-empty-value">não</span>}
                                            </span>
                                            <span className="lk-cell">
                                                {c.tipo !== TipoCampoPersonalizado.Selecao
                                                    ? <span className="lk-empty-value">—</span>
                                                    : ops.length === 0
                                                        ? <span className="cp-tag is-alerta">sem opções</span>
                                                        : <span>
                                                            {ops.slice(0, 2).map(o => <span className="lk-tag" key={o}>{o}</span>)}
                                                            {ops.length > 2 && <span className="lk-tag">+{ops.length - 2}</span>}
                                                          </span>}
                                            </span>
                                            <RowActions>
                                                <RowAction icon={<LuEye size={14} />} label="Ver campo"
                                                    onClick={() => abrirVer(c)} />
                                                <RowAction icon={<LuTrash2 size={14} />} label="Excluir campo" danger
                                                    onClick={() => setAConfirmar(c.campoPersonalizadoId)} />
                                            </RowActions>
                                        </ListRow>
                                    );
                                })}
                        </div>
                    </div>

                    {!isLoading && filtrados.length === 0 && listaVazia}
                    {!isLoading && filtrados.length > 0 && rodape}
                </div>
                )}
            </div>

            <RecordPanel
                open={!!aberto}
                onClose={fechar}
                title={modo === 'editar'
                    ? (rascunho.nome.trim() || (ehNovo ? 'Novo campo' : 'Campo'))
                    : (aberto?.nome ?? '')}
                subtitle={aberto && aberto.campoPersonalizadoId
                    ? `${rotuloTipo(aberto.tipo)}${aberto.obrigatorio ? ' · obrigatório' : ''}`
                    : undefined}
                editing={modo === 'editar'}
                onBack={ehNovo ? undefined : () => setModo('ver')}

                actions={aberto && aberto.campoPersonalizadoId && (
                    <button type="button" className="rp-btn is-primary" onClick={() => abrirEdicao(aberto)}>
                        <LuPencil size={13} /> Editar
                    </button>
                )}
                footer={
                    <>
                        {!ehNovo && (
                            <button type="button" className="rp-btn is-danger" onClick={() => setConfirmarNoPainel(true)} disabled={salvando}>
                                Excluir
                            </button>
                        )}
                        <button type="button" className="rp-btn" disabled={salvando}
                            onClick={() => (ehNovo ? fechar() : setModo('ver'))}>Cancelar</button>
                        <button type="button" className="rp-btn is-primary" onClick={salvar} disabled={salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    </>
                }
            >
                {modo === 'ver' && aberto && aberto.campoPersonalizadoId && (
                    <>
                        <PanelSection title="Definição">
                            <div className="rp-kv">
                                <span className="rp-k">Nome</span>
                                <span className="rp-v">{aberto.nome}</span>
                                <span className="rp-k">Chave</span>
                                <span className="rp-v cp-chave">{aberto.chave || '—'}</span>
                                <span className="rp-k">Tipo</span>
                                <span className="rp-v">{rotuloTipo(aberto.tipo)}</span>
                                <span className="rp-k">Obrigatório</span>
                                <span className="rp-v">{aberto.obrigatorio ? 'Sim' : 'Não'}</span>
                                <span className="rp-k">Ordem</span>
                                <span className="rp-v">{aberto.ordem ?? 0}</span>
                            </div>
                        </PanelSection>

                        {aberto.tipo === TipoCampoPersonalizado.Selecao && (
                            <PanelSection title="Opções">
                                {(aberto.opcoes ?? []).length === 0
                                    ? <p className="rp-none">Sem opções — o campo aparece vazio no negócio.</p>
                                    : <div className="cp-chips">
                                        {(aberto.opcoes ?? []).map(o => <span className="cp-chip" key={o}>{o}</span>)}
                                      </div>}
                            </PanelSection>
                        )}
                    </>
                )}

                {modo === 'editar' && (
                    <>
                        <PanelSection title="Definição">
                            <div className="rp-field">
                                <label className="rp-label" htmlFor="cp-nome">Nome do campo</label>
                                <input id="cp-nome" className="rp-input" type="text" autoFocus
                                    value={rascunho.nome} placeholder="Ex.: Número do contrato"
                                    onChange={e => { setRascunho(r => ({ ...r, nome: e.target.value })); setErros({ ...erros, nome: '' }); }} />
                                {erros.nome
                                    ? <span className="rp-error">{erros.nome}</span>
                                    : <span className="cp-hint">É o rótulo que aparece no cadastro do negócio.</span>}
                            </div>

                            {!ehNovo && aberto?.chave && (
                                /* A chave é gerada pela API a partir do nome e não muda depois:
                                   é ela que amarra os valores já preenchidos. */
                                <div className="rp-field">
                                    <span className="rp-label">Chave</span>
                                    <p className="cp-chave cp-fixa">{aberto.chave}</p>
                                    <span className="cp-hint">
                                        Gerada no cadastro e fixa — é o que liga este campo aos valores já preenchidos.
                                    </span>
                                </div>
                            )}

                            <div className="rp-field">
                                <label className="rp-label" htmlFor="cp-tipo">Tipo</label>
                                <Select id="cp-tipo" value={String(rascunho.tipo)}
                                    options={TIPOS.map(t => ({ value: String(t.valor), label: t.rotulo }))}
                                    onChange={v => setRascunho(r => ({ ...r, tipo: Number(v) as TipoCampoPersonalizado }))} />
                                <span className="cp-hint">
                                    {TIPOS.find(t => t.valor === rascunho.tipo)?.ajuda}
                                </span>
                            </div>

                            <div className="rp-toggle-row">
                                <div className="rp-toggle-text">
                                    <span className="rp-toggle-label">Obrigatório</span>
                                    <span className="rp-toggle-hint">
                                        O negócio não pode ser salvo sem este campo preenchido.
                                    </span>
                                </div>
                                <Switch
                                    checked={rascunho.obrigatorio}
                                    label="Campo obrigatório"
                                    onChange={v => setRascunho(r => ({ ...r, obrigatorio: v }))}
                                />
                            </div>
                        </PanelSection>

                        {ehSelecao && (
                            <PanelSection title="Opções">
                                <div className="cp-chips">
                                    {rascunho.opcoes.map((o, i) => (
                                        <span className="cp-chip" key={o}>
                                            {o}
                                            <button type="button" className="cp-chip-x" aria-label={`Remover ${o}`}
                                                onClick={() => setRascunho(r => ({ ...r, opcoes: r.opcoes.filter((_, x) => x !== i) }))}>
                                                <LuX size={11} />
                                            </button>
                                        </span>
                                    ))}
                                </div>

                                <div className="cp-add">
                                    <input type="text" className="rp-input" placeholder="Ex.: Boleto"
                                        value={novaOpcao}
                                        onChange={e => setNovaOpcao(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); adicionarOpcao(); } }} />
                                    <button type="button" className="rp-btn" onClick={adicionarOpcao}>
                                        <LuPlus size={13} /> Adicionar
                                    </button>
                                </div>

                                {erros.opcoes && (
                                    <p className="cp-alerta"><LuInfo size={13} /> {erros.opcoes}</p>
                                )}
                            </PanelSection>
                        )}
                    </>
                )}

                {/* Histórico: seção no fim do modo ver (painel sem abas). */}
                {modo === 'ver' && aberto?.campoPersonalizadoId && (
                    <SecaoHistorico entidade="campo_personalizado" id={aberto.campoPersonalizadoId} />
                )}
            </RecordPanel>

            <Dialog
                open={confirmarNoPainel}
                onClose={() => setConfirmarNoPainel(false)}
                title={`Excluir o campo "${rascunho.nome}"?`}
                confirmLabel="Excluir campo"
                danger
                busy={salvando}
                onConfirm={() => excluir()}
            >
                O campo sai do formulário do negócio. Os valores já preenchidos continuam
                gravados, mas deixam de aparecer na tela.
            </Dialog>
        </div>
    );
};

export default CamposPersonalizadosPage;
